import { FC } from "react";
import { Link } from "react-router-dom";
import classes from "./teamDetails.module.scss";
import { ITeam, ITeamStaff, ITeamTgAccount } from "../../../../entities/team";
import { MyButton } from "../../../../shared/ui/button";
import { STAFF_ROUTE } from "../../../../app/router/routes";
import { TEAM_ACTION_DELETE, TEAM_ACTION_UPDATE } from "../../lib/types";
import { useAppSelector } from "../../../../app/store/store";
import { FEATURES, FEATURE_SECTIONS, hasFeature } from "../../../../entities/my";

interface IProps {
    team: ITeam;
    canOpenEditor: boolean;
    onEdit: (teamId: string) => void;
    onDelete: (team: ITeam) => void;
}

export const TeamDetails: FC<IProps> = ({team, canOpenEditor, onEdit, onDelete}) => {
    const {my} = useAppSelector(state => state.myReducer)
    const canEdit = canOpenEditor && team.availableActions.includes(TEAM_ACTION_UPDATE)
    const canDelete = team.availableActions.includes(TEAM_ACTION_DELETE)

    const getStaffName = (staff: ITeamStaff) => staff.fullName || `User #${staff.userId}`
    const getTgName = (tg: ITeamTgAccount) => tg.title || tg.username || `TG #${tg.id}`
    const getStaffPath = (userId: string) => `${STAFF_ROUTE.path}/${userId}`
    const getAuditActorLabel = (fullName?: string, userId?: string) => {
        if(fullName?.trim()) return fullName
        if(userId?.trim()) return `#${userId}`
        return ''
    }
    const getStaff = (staff: ITeamStaff) => hasFeature(my, FEATURE_SECTIONS.STAFF)
        && hasFeature(my, FEATURES.IDM.STAFF_READ, Number(staff.userId))
        ? <Link className={classes.staffLink} to={getStaffPath(staff.userId)}>{getStaffName(staff)}</Link>
        : <>{getStaffName(staff)}</>

    return (
        <section className={classes.panel}>
            <section className={classes.detailHeader}>
                <section>
                    <section className={classes.detailTitle}>{team.name}</section>
                    <section className={classes.detailSub}>ID: {team.id}</section>
                </section>
                <section className={classes.detailActions}>
                    {canEdit && <MyButton onClick={() => onEdit(team.id)}>Редактировать</MyButton>}
                    {canDelete && (
                        <button
                            type="button"
                            className={classes.dangerButton}
                            aria-label="Архивировать звено"
                            onClick={() => onDelete(team)}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 3H15L16 5H21V7H3V5H8L9 3Z" fill="currentColor"></path>
                                <path d="M6 9H18L17 20C16.95 20.57 16.48 21 15.91 21H8.09C7.52 21 7.05 20.57 7 20L6 9Z" fill="currentColor"></path>
                                <path d="M10 11V17" stroke="white" strokeWidth="1.8" strokeLinecap="round"></path>
                                <path d="M14 11V17" stroke="white" strokeWidth="1.8" strokeLinecap="round"></path>
                            </svg>
                        </button>
                    )}
                </section>
            </section>

            <section className={classes.grid}>
                <section className={classes.infoCard}>
                    <section className={classes.infoLabel}>Рабочие ТГ</section>
                    <section className={classes.infoValue}>
                        {team.tgAccounts.map(tg => getTgName(tg)).join(', ') || 'Не указаны'}
                    </section>
                </section>
                <section className={classes.infoCard}>
                    <section className={classes.infoLabel}>Координаторы звена</section>
                    <section className={classes.infoValue}>
                        {
                            team.coordinators.length > 0
                                ? team.coordinators.map((coordinator, index) => (
                                    <span key={coordinator.userId}>
                                        {getStaff(coordinator)}
                                        {index < team.coordinators.length - 1 ? ', ' : ''}
                                    </span>
                                ))
                                : 'Не указаны'
                        }
                    </section>
                </section>
            </section>

            <section className={classes.structure}>
                {team.tgCurators.map(item => (
                    <section key={item.tgAccount.id} className={classes.structureCard}>
                        <section className={classes.structureTitle}>{getTgName(item.tgAccount)}</section>
                        <section className={classes.itemMeta}>
                            Координатор: {
                                item.coordinator
                                    ? getStaff(item.coordinator)
                                    : 'Не указан'
                            }
                        </section>
                        <section className={classes.chips}>
                            {item.curators.length > 0
                                ? item.curators.map(curator => (
                                    <section key={`${item.tgAccount.id}-${curator.userId}`} className={classes.chip}>
                                        {getStaff(curator)}
                                    </section>
                                ))
                                : <section className={classes.chip}>Кураторы не указаны</section>}
                        </section>
                    </section>
                ))}
            </section>

            {(team.audit?.createdAt
                || team.audit?.updatedAt
                || team.audit?.createdByUserId
                || team.audit?.updatedByUserId
                || team.audit?.created_by_user_id
                || team.audit?.updated_by_user_id
                || team.audit?.createdByFullName
                || team.audit?.updatedByFullName
                || team.audit?.created_by_full_name
                || team.audit?.updated_by_full_name) && (
                <section className={classes.audit}>
                    {team.audit?.createdAt && <section>Создано: {team.audit.createdAt}</section>}
                    {getAuditActorLabel(
                        team.audit?.createdByFullName || team.audit?.created_by_full_name,
                        team.audit?.createdByUserId || team.audit?.created_by_user_id
                    ) && (
                        <section>
                            Автор создания: {getAuditActorLabel(
                                team.audit?.createdByFullName || team.audit?.created_by_full_name,
                                team.audit?.createdByUserId || team.audit?.created_by_user_id
                            )}
                        </section>
                    )}
                    {team.audit?.updatedAt && <section>Обновлено: {team.audit.updatedAt}</section>}
                    {getAuditActorLabel(
                        team.audit?.updatedByFullName || team.audit?.updated_by_full_name,
                        team.audit?.updatedByUserId || team.audit?.updated_by_user_id
                    ) && (
                        <section>
                            Последнее изменение: {getAuditActorLabel(
                                team.audit?.updatedByFullName || team.audit?.updated_by_full_name,
                                team.audit?.updatedByUserId || team.audit?.updated_by_user_id
                            )}
                        </section>
                    )}
                </section>
            )}
        </section>
    )
}
